import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useLocation, withRouter } from "react-router-dom";
import { FiFileText, FiAlertCircle, FiArrowLeft, FiMaximize, FiMinimize } from "react-icons/fi";
import { FaFileExcel, FaFileWord, FaFileAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import axios from "axios";
import MyYuukke from "../../../api/MyYuukke";
import { API_URL } from "../../../config/app-config";
import "./ServiceResource.css";

const previewUrl = (fileName) => `${API_URL}auth/resource/file/${fileName}/preview`;

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const DOC_TYPE_META = {
  PDF: { icon: FiFileText, label: "PDF Document", renderMode: "pdf" },
  EXCEL: { icon: FaFileExcel, label: "Excel Spreadsheet", renderMode: "pdf" },
  AGREEMENT: { icon: FaFileWord, label: "Word Document", renderMode: "pdf" },
  ODT: { icon: FaFileAlt, label: "OpenDocument Text", renderMode: "pdf" },
  OTHER: { icon: FiFileText, label: "File", renderMode: "download" },
};

const metaFor = (docType) => DOC_TYPE_META[docType] || DOC_TYPE_META.OTHER;

export const ViewDocument = ({ history }) => {
  const { slug } = useParams();
  const location = useLocation();

  const [resource, setResource] = useState(location.state?.resource || null);
  const [loading, setLoading] = useState(!location.state?.resource);
  const [error, setError] = useState(null);

  const [renderCache, setRenderCache] = useState({});
  const blobUrlsRef = useRef([]);
  const containerRefs = useRef({});
  const [fullscreenFile, setFullscreenFile] = useState(null);

  useEffect(() => {
    return () => blobUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
  }, []);

  // ── Fullscreen change listener — keeps state in sync if the user exits
  // via Esc or the browser's own fullscreen control, not just our button.
  useEffect(() => {
    const handleFsChange = () => {
      if (!document.fullscreenElement) setFullscreenFile(null);
    };
    document.addEventListener("fullscreenchange", handleFsChange);
    return () => document.removeEventListener("fullscreenchange", handleFsChange);
  }, []);

  useEffect(() => {
    if (resource || !slug) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    MyYuukke.getResourceDownload(slug)
      .then((res) => {
        if (cancelled) return;
        const payload = res?.data?.data ?? res?.data;
        if (!payload) {
          setError("Document not found.");
          setLoading(false);
          return;
        }
        setResource(payload);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        setError(status === 404 ? "This resource doesn't exist or was removed." : "This document couldn't be loaded.");
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [slug, resource]);

  const files = resource?.resourceFiles || [];

  const loadFile = useCallback(async (file, meta) => {
    if (!file || meta.renderMode === "download") return;
    setRenderCache((prev) => ({ ...prev, [file.fileName]: { status: "loading" } }));

    try {
      const res = await axios.get(previewUrl(file.fileName), {
        responseType: "blob",
        headers: getAuthHeaders(),
      });
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      blobUrlsRef.current.push(blobUrl);
      setRenderCache((prev) => ({ ...prev, [file.fileName]: { status: "ready", blobUrl, blob: res.data } }));
    } catch (err) {
      console.error(`Failed to load preview for ${file.fileName}:`, err?.response?.status, err?.message);
      setRenderCache((prev) => ({ ...prev, [file.fileName]: { status: "error" } }));
    }
  }, []);

  useEffect(() => {
    files.forEach((file) => {
      const meta = metaFor(file.docType);
      if (meta.renderMode !== "download" && !renderCache[file.fileName]) {
        loadFile(file, meta);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resource]);

  // ── Fullscreen toggle for one file's viewer container ──
  const toggleFullscreen = (fileName) => {
    const el = containerRefs.current[fileName];
    if (!el) return;

    if (!document.fullscreenElement) {
      el.requestFullscreen?.().then(() => setFullscreenFile(fileName)).catch((err) => {
        console.error("Failed to enter fullscreen:", err);
        toast.error("Fullscreen isn't available in this browser.");
      });
    } else {
      document.exitFullscreen?.();
      setFullscreenFile(null);
    }
  };

  const renderFallback = (meta, message) => (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "12px", padding: "3rem 1.5rem", textAlign: "center" }}>
      <meta.icon size={40} color="#9ca3af" />
      <p style={{ margin: 0, color: "#6b7280", fontSize: "0.9rem" }}>
        {message || `${meta.label} files can't be previewed here.`}
      </p>
    </div>
  );

  const renderActionRow = (file, meta) => {
    const isFullscreen = fullscreenFile === file.fileName;
    return (
      <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", padding: "10px 14px", borderTop: "1px solid #e5e7eb", background: "#f9fafb" }}>
        <button
          type="button"
          onClick={() => toggleFullscreen(file.fileName)}
          style={actionBtnStyle}
          title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
        >
          {isFullscreen ? <FiMinimize size={14} /> : <FiMaximize size={14} />}
          {isFullscreen ? "Exit fullscreen" : "Fullscreen"}
        </button>
      </div>
    );
  };

  const renderFile = (file, index) => {
    const meta = metaFor(file.docType);
    const cache = renderCache[file.fileName];
    const renderedOk = cache?.status === "ready";

    return (
      <div key={file.fileName + index} style={{ marginBottom: "1.5rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "0.5rem" }}>
          <meta.icon size={16} color="var(--svc-brand, #e11d48)" />
          <span style={{ fontWeight: 700, fontSize: "0.9rem" }}>
            {meta.label} {files.length > 1 ? `— File ${index + 1} of ${files.length}` : ""}
          </span>
        </div>

        <div
          ref={(el) => { containerRefs.current[file.fileName] = el; }}
          style={{ border: "1px solid #e5e7eb", borderRadius: "12px", overflow: "hidden", background: "#000" }}
        >
          {meta.renderMode === "download" && renderFallback(meta)}

          {meta.renderMode !== "download" && (!cache || cache.status === "loading") && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "12px", padding: "3rem 1.5rem", background: "#f9fafb" }}>
              <div className="svrs-spinner" aria-hidden="true" />
              <p style={{ margin: 0, color: "#6b7280", fontSize: "0.9rem" }}>Loading {meta.label}…</p>
            </div>
          )}

          {cache?.status === "error" && (
            <div style={{ background: "#f9fafb" }}>
              {renderFallback(meta, "Couldn't load this file for preview.")}
            </div>
          )}

          {renderedOk && (
            <>
              <iframe
                src={`${cache.blobUrl}#toolbar=0&navpanes=0`}
                title={meta.label}
                style={{
                  width: "100%",
                  height: fullscreenFile === file.fileName ? "100vh" : "80vh",
                  border: "none",
                  display: "block",
                  background: "#fff",
                }}
              />
              {renderActionRow(file, meta)}
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="svrs-stage">
      <div className="svrs-vignette" aria-hidden="true" />

      {loading && (
        <div className="svrs-ticket svrs-ticket--loading" role="status" aria-live="polite">
          <div className="svrs-spinner" aria-hidden="true" />
          <p>Loading your documents…</p>
        </div>
      )}

      {!loading && (error || !resource) && (
        <div className="svrs-ticket svrs-ticket--error" role="alert">
          <FiAlertCircle size={28} />
          <h2>Can't open this document</h2>
          <p>{error || "Document not found."}</p>
        </div>
      )}

      {!loading && resource && (
        <div className="svrs-ticket" style={{ maxWidth: "900px" }}>
          <div className="svrs-perf svrs-perf--top" aria-hidden="true" />
          <header className="svrs-head" style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "1rem" }}>
            <div>
              <span className="svrs-eyebrow">Your files</span>
              <h1 className="svrs-title">{resource.serviceName}</h1>
            </div>
            <button
              type="button"
              onClick={() => history.goBack()}
              aria-label="Go back"
              style={{ background: "transparent", border: "1px solid #e5e7eb", borderRadius: "8px", padding: "8px 12px", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px", fontSize: "0.85rem", fontWeight: 600 }}
            >
              <FiArrowLeft size={14} /> Back
            </button>
          </header>

          {files.length === 0 ? (
            <div className="svrs-empty">No files attached to this resource.</div>
          ) : (
            <div style={{ marginTop: "1rem" }}>{files.map((file, i) => renderFile(file, i))}</div>
          )}
          <div className="svrs-perf svrs-perf--bottom" aria-hidden="true" />
        </div>
      )}
    </div>
  );
};

const actionBtnStyle = {
  display: "flex", alignItems: "center", gap: "6px",
  padding: "8px 14px", borderRadius: "8px", border: "1px solid var(--svc-brand, #e11d48)",
  background: "#fff", color: "var(--svc-brand, #e11d48)",
  fontSize: "0.8rem", fontWeight: 700, cursor: "pointer",
};

export default withRouter(ViewDocument);